import { PureComponent } from 'react';
import { connect } from 'react-redux';

import clone from 'lodash/clone';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import toLower from 'lodash/toLower';
import PropTypes from 'prop-types';

import CreateMessageComponent from '../components/CreateMessageComponent';

import { getBuildingsForFilter } from '../../Building/actionCreators';
import {
  clearRecipients,
  getRecipients,
  getRoles,
  getThreadsScopes,
  handleNewChatCreation,
} from '../actions';

import { formatSelect } from '../../Forms/utils';
import { getFullName } from '../../Person/utils';

const getDefaultSelectValue = () => ({
  value: 'all_properties',
  label: 'All properties',
});
class CreateMessageContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectOptions: [],
      selectedValue: getDefaultSelectValue(),
      search: '',

      isRecipientPerson: true,
      messageTo: [], // person or groups names, used in title

      checkedRecipients: [],
      selectedThreadsScopes: [],

      filteredThreadsScopes: null,
    };
    this.timeoutId = null;
    this.onPersonsOrGroupsChecked = this.onPersonsOrGroupsChecked.bind(this);
    this.onAddRecipient = this.onAddRecipient.bind(this);
    this.loadNextRecipients = this.loadNextRecipients.bind(this);
    this.createNewChat = this.createNewChat.bind(this);
    this.onSelectChangeAndRefresh = this.onSelectChangeAndRefresh.bind(this);
    this.getSelectOptions = this.getSelectOptions.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.insertBuildingName = this.insertBuildingName.bind(this);
  }

  async componentDidMount() {
    const { getBuildings, getRoles, getRecipients, getThreadsScopes } =
      this.props;
    const buildings = getBuildings();
    const roles = getRoles();
    const recipients = getRecipients();
    const threadsScopes = getThreadsScopes();
    await Promise.all([buildings, roles, recipients, threadsScopes]);
    this.getSelectOptions();
  }

  componentDidUpdate(prevProps, prevState) {
    const { search, isRecipientPerson } = this.state;
    const { search: prevSearch, isRecipientPerson: prevIsRecipientPerson } =
      prevState;
    // run search only when there is a new value and it's not a tab change
    if (search !== prevSearch && isRecipientPerson === prevIsRecipientPerson) {
      this.onSearch(search);
    }
  }

  componentWillUnmount() {
    const { clearRecipients } = this.props;
    clearRecipients();
  }

  // used in the input component
  onSearchChange = (e) => this.setState({ search: e.target.value });

  onSearch = (search) => {
    const { isRecipientPerson, selectedValue } = this.state;
    const { clearRecipients, getRecipients } = this.props;

    if (isRecipientPerson) {
      clearTimeout(this.timeoutId); // 'debounce'
      this.timeoutId = setTimeout(() => {
        clearRecipients();
        const requestData = {
          q: search,
          building: !isEqual(selectedValue, getDefaultSelectValue())
            ? selectedValue.value
            : null,
        };
        getRecipients(requestData);
      }, 800);
      return null;
    }

    const filteredThreadsScopes = this.filterThreadsScopes(
      search,
      selectedValue,
    );
    return this.setState({ filteredThreadsScopes });
  };

  onSelectChangeAndRefresh(event, selectedValue) {
    const { isRecipientPerson, search } = this.state;
    const { clearRecipients, getRecipients } = this.props;

    // reset to default
    if (!selectedValue || isEqual(selectedValue, getDefaultSelectValue())) {
      this.setState({
        selectedValue: getDefaultSelectValue(),
      });
      if (isRecipientPerson) {
        clearRecipients();
        getRecipients({ q: search });
        return null;
      }
      const filteredThreadsScopes = this.filterThreadsScopes(
        search,
        getDefaultSelectValue(),
      );
      return this.setState({ filteredThreadsScopes });
    }

    // set new value
    this.setState({ selectedValue });
    if (isRecipientPerson) {
      clearRecipients();
      getRecipients({ building: selectedValue.value, q: search });
      return null;
    }
    const filteredThreadsScopes = this.filterThreadsScopes(
      search,
      selectedValue,
    );
    return this.setState({ filteredThreadsScopes });
  }

  // used on tab change
  onPersonsOrGroupsChecked(type) {
    const { selectedValue } = this.state;
    const { threadsScopes } = this.props;

    const isRecipientPerson = type === 'persons';

    // reset values when tab is changed
    this.setState(
      {
        isRecipientPerson,
        checkedRecipients: [],
        selectedThreadsScopes: [],
        messageTo: [],
        search: '',
        filteredThreadsScopes: threadsScopes,
      },
      () => this.onSelectChangeAndRefresh(null, selectedValue),
    );
  }

  // add person id to the 'selected recipients' arr and person name to the 'message to' arr
  // if selected person is already there -> remove person's data from the mentioned above
  onAddRecipient(recipient) {
    const { checkedRecipients, messageTo } = this.state;
    const { id } = recipient;
    const fullName = getFullName(recipient);

    let checked = [...checkedRecipients];
    let checkedNames = [...messageTo];
    const isAlreadyExist = checked.indexOf(id);

    if (id && isAlreadyExist < 0) {
      checked = [...checked, id];
      checkedNames = [...checkedNames, fullName];
    } else if (id) {
      checked = checked.filter((item) => item !== id);
      checkedNames = checkedNames.filter(
        (checkedName) => checkedName !== fullName,
      );
    }
    this.setState({
      checkedRecipients: checked,
      messageTo: checkedNames,
    });
  }

  onAddGroup = (scope, buildingId) => {
    const { selectedThreadsScopes, messageTo } = this.state;
    const { threadsScopes } = this.props;
    const { id, name, isAllBuildings } = scope;

    // if all buildings selected -> send all scopes with isAllBuildingScope = true
    if (isAllBuildings) {
      const groupsWithAllBuildingScope = [];
      Object.keys(threadsScopes).forEach((buildingId) => {
        if (Number(buildingId) === 0) return null;
        threadsScopes[buildingId].forEach(({ id, isAllBuildingScope }) => {
          isAllBuildingScope &&
            groupsWithAllBuildingScope.push({
              scope: id,
              building: buildingId,
            });
        });
        return null;
      });
      return this.setState(
        {
          selectedThreadsScopes: groupsWithAllBuildingScope,
          messageTo: ['All Buildings'],
        },
        () => this.createNewChat(),
      );
    }

    // if item was already selected -> remove it
    const scopeIndex = selectedThreadsScopes.findIndex(
      ({ scope: scopeId, building }) =>
        scopeId === id && building === buildingId,
    );
    if (scopeIndex >= 0) {
      const newSelectedThreadsScopes = [...selectedThreadsScopes];
      newSelectedThreadsScopes.splice(scopeIndex, 1);

      const nameIndex = messageTo.findIndex((scopeName) => scopeName !== name);
      const newMessageTo = [...messageTo];
      newMessageTo.splice(nameIndex, 1);

      return this.setState({
        selectedThreadsScopes: newSelectedThreadsScopes,
        messageTo: newMessageTo,
      });
    }

    // if it's a new item -> add to the array
    return this.setState({
      selectedThreadsScopes: [
        ...selectedThreadsScopes,
        { scope: id, building: buildingId },
      ],
      messageTo: [...messageTo, name],
    });
  };

  // sets initial select values
  getSelectOptions() {
    const { buildings } = this.props;
    const defaultOption = [getDefaultSelectValue()];
    const buildingOptions = formatSelect(Object.values(buildings));
    this.setState({ selectOptions: [...defaultOption, ...buildingOptions] });
  }

  filterThreadsScopes = (searchValue, filterValue) => {
    const { buildings, threadsScopes } = this.props;

    if (!Object.keys(threadsScopes)?.length) return {};

    const filteredThreadsScopes = {};

    // if there is a filter on and current value !== to the default value -> use only filtered values for search
    const buildingsThreadsScopes =
      !isEqual(filterValue, getDefaultSelectValue()) &&
      threadsScopes?.[filterValue?.value]
        ? { [filterValue?.value]: threadsScopes?.[filterValue?.value] }
        : null;
    const initialThreadsScopes = buildingsThreadsScopes || threadsScopes;

    if (!searchValue) return initialThreadsScopes;

    // 1. search among building names
    // 2. search among scopes
    const search = toLower(searchValue);
    Object.keys(initialThreadsScopes).forEach((buildingId) => {
      const buildingName = buildings?.[buildingId]?.name;
      const buildingScopes = initialThreadsScopes[buildingId];
      if (toLower(buildingName).includes(search)) {
        filteredThreadsScopes[buildingId] = buildingScopes;
        return buildingScopes;
      }
      buildingScopes.forEach((scope) => {
        if (toLower(scope.name).includes(search)) {
          Array.isArray(filteredThreadsScopes[buildingId])
            ? filteredThreadsScopes[buildingId].push(scope)
            : (filteredThreadsScopes[buildingId] = [scope]);
        }
      });
      return null;
    });
    return filteredThreadsScopes;
  };

  // update Redux state with new data for a thread creation
  // and close modal window
  createNewChat() {
    const { checkedRecipients, selectedThreadsScopes, messageTo } = this.state;
    const { handleNewChatCreation, closeModal } = this.props;

    const data = {
      recipients: checkedRecipients,
      groups: selectedThreadsScopes,
      messageTo: [...new Set(messageTo)], // there might be the same group names from different buildings
    };

    handleNewChatCreation(data);
    closeModal();
  }

  loadNextRecipients() {
    const { selectedValue } = this.state;
    const { nextRecipients, getRecipients } = this.props;

    if (isEmpty(nextRecipients)) return null;

    if (selectedValue.value === 'all_properties') {
      return getRecipients(nextRecipients);
    }

    return getRecipients({
      building: selectedValue.value,
      ...nextRecipients,
    });
  }

  insertBuildingName(list) {
    // when 'all_properties' filter value is set all persons (recipients) are grouped by building
    // here we add building name above the first person from this building

    const { selectedValue } = this.state;
    const { buildings } = this.props;

    if (selectedValue.value !== 'all_properties') {
      return list;
    }
    const uniqueBuildingsFromList = [
      ...new Set(list.map((element) => element.building)),
    ];
    const filteredBuildings = Object.values(buildings).filter(
      (building) => uniqueBuildingsFromList.indexOf(building.id) !== -1,
    );
    const buildingsId = filteredBuildings.map((building) => building.id);
    const filteredList = list.filter(
      (element) => buildingsId.indexOf(element.building) !== -1,
    );
    if (!isEmpty(filteredList) && !isEmpty(filteredBuildings)) {
      const listWithBuildings = map(filteredList, clone);
      filteredBuildings.map((building) => {
        const index = listWithBuildings.findIndex(
          (recipient) => recipient.buildingName === building.name,
        );

        listWithBuildings.splice(index, 0, {
          buildingDisplayName: building.name,
        });
        return false;
      });
      return listWithBuildings;
    }
    return null;
  }

  render() {
    const {
      selectOptions,
      selectedValue,
      search,
      isRecipientPerson,
      checkedRecipients,
      filteredThreadsScopes,
      selectedThreadsScopes,
    } = this.state;
    const {
      recipients,
      isLoading,
      nextRecipients,
      roles,
      buildings,
      buildingId,
      threadsScopes,
    } = this.props;

    const groups =
      isEmpty(filteredThreadsScopes) && !search
        ? threadsScopes
        : filteredThreadsScopes;
    const groupKeys = groups && Object.keys(groups);
    const isGroups = !isRecipientPerson;
    const showLoader = isGroups
      ? isLoading && !groupKeys?.length > 0
      : isLoading && !recipients.length > 0;
    const showNotFound = isGroups
      ? !isLoading && !groupKeys?.length > 0
      : !isLoading && !recipients.length > 0;
    const hasSelectedRecipients = isGroups
      ? selectedThreadsScopes.length > 0
      : checkedRecipients.length > 0;
    const hasTwoOrMoreBuildings = Object.keys(buildings).length > 1;

    const props = {
      buildings,
      buildingId,

      selectedValue,
      selectOptions,
      onSelectChangeAndRefresh: this.onSelectChangeAndRefresh,

      isGroups,
      onPersonsOrGroupsChecked: this.onPersonsOrGroupsChecked,

      onSearchChange: this.onSearchChange,
      search,

      groupKeys,
      showLoader,
      showNotFound,
      hasSelectedRecipients,
      hasTwoOrMoreBuildings,

      isLoading,
      recipients,
      groups,
      nextRecipients,
      loadNextRecipients: this.loadNextRecipients,
      onAddRecipient: this.onAddRecipient,
      onAddGroup: this.onAddGroup,
      checkedRecipients,
      checkedGroups: selectedThreadsScopes,
      roles,
      insertBuildingName: this.insertBuildingName,
      handleNewChat: this.createNewChat,
    };
    return <CreateMessageComponent {...props} />;
  }
}

const mapStateToProps = (state, ownProps) => {
  const { getRecipients, checkedRecipients, getRoles, buildings, messages } =
    state;
  const buildingId = get(ownProps, 'buildingId', null);
  return {
    checkedRecipients,
    recipients: get(getRecipients, 'results', []),
    threadsScopes: messages.threadsScopes,
    currentUser: get(state, 'common.currentUser', {}),
    nextRecipients: get(getRecipients, 'next', {}),
    isLoading: !!state.common.loading.length,
    roles: getRoles,
    buildings,
    buildingId,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getBuildings: () => getBuildingsForFilter(dispatch),
  getRecipients: (queryParams) => {
    const params = { ...queryParams, limit: 10 };
    const buildingId = get(ownProps, 'buildingId', null);
    if (buildingId) {
      params.building = buildingId;
    }
    return getRecipients(dispatch, params);
  },
  getThreadsScopes: () => getThreadsScopes(dispatch),
  getRoles: () => getRoles(dispatch),
  clearRecipients: () => clearRecipients(dispatch),
  handleNewChatCreation: (data) => handleNewChatCreation(dispatch, data),
});

CreateMessageContainer.defaultProps = {
  checkedRecipients: null,
  nextRecipients: null,
};

CreateMessageContainer.propTypes = {
  getBuildings: PropTypes.func.isRequired,
  getThreadsScopes: PropTypes.func.isRequired,
  threadsScopes: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  checkedRecipients: PropTypes.instanceOf(Array),
  recipients: PropTypes.instanceOf(Array).isRequired,
  currentUser: PropTypes.instanceOf(Object).isRequired,
  nextRecipients: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool.isRequired,
  roles: PropTypes.instanceOf(Array).isRequired,
  getRecipients: PropTypes.func.isRequired,
  getRoles: PropTypes.func.isRequired,
  clearRecipients: PropTypes.func.isRequired,
  handleNewChatCreation: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  buildingId: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateMessageContainer);
